'use client';
import React, { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface TickerCoin {
  id: string;
  symbol: string;
  name: string;
  price: string;
  change: string;
  positive: boolean;
}

const TICKER_SYMBOLS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE', 'AVAX', 'DOT', 'LINK'];

const COIN_NAMES: Record<string, string> = {
  BTC: 'Bitcoin', ETH: 'Ethereum', SOL: 'Solana', BNB: 'BNB',
  XRP: 'XRP', ADA: 'Cardano', DOGE: 'Dogecoin', AVAX: 'Avalanche',
  DOT: 'Polkadot', LINK: 'Chainlink',
};

function formatPrice(price: number): string {
  if (price >= 1000) return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (price >= 1) return '$' + price.toFixed(2);
  if (price >= 0.01) return '$' + price.toFixed(4);
  return '$' + price.toFixed(8);
}

function formatChange(change: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}

const staticTickerCoins: TickerCoin[] = TICKER_SYMBOLS.map((sym, i) => ({
  id: `ticker-${sym.toLowerCase()}`,
  symbol: sym,
  name: COIN_NAMES[sym] ?? sym,
  price: '—',
  change: '—',
  positive: true,
}));

export default function MarketTicker() {
  const [tickerCoins, setTickerCoins] = useState<TickerCoin[]>(staticTickerCoins);
  const [fearGreed] = useState('72 (Greed)');

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch('/api/crypto/market-data');
        if (!res.ok) return;
        const json = await res.json();
        const coins: TickerCoin[] = TICKER_SYMBOLS.map((sym) => {
          const coin = json.coins?.find((c: { symbol: string }) => c.symbol === sym);
          if (!coin) return staticTickerCoins.find((s) => s.symbol === sym)!;
          return {
            id: `ticker-${sym.toLowerCase()}`,
            symbol: sym,
            name: COIN_NAMES[sym] ?? sym,
            price: formatPrice(coin.price),
            change: formatChange(coin.change24h),
            positive: coin.change24h >= 0,
          };
        });
        setTickerCoins(coins);
      } catch {
        // keep static fallback
      }
    }
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-full bg-black border-b border-border overflow-hidden">
      <div className="flex items-center h-9">
        <div className="flex-shrink-0 flex items-center gap-2 px-4 bg-card border-r border-border h-full">
          <span className="w-2 h-2 rounded-full bg-accent live-pulse flex-shrink-0" />
          <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider whitespace-nowrap">
            Market Update
          </span>
        </div>

        {/* Scrolling ticker — pauses on hover */}
        <div
          className="flex-1 overflow-hidden relative group"
          style={{ cursor: 'default' }}
        >
          <div
            className="flex gap-0 ticker-scroll group-hover:[animation-play-state:paused]"
            style={{ width: 'max-content' }}
          >
            {[...tickerCoins, ...tickerCoins]?.map((coin, i) => (
              <div
                key={`tick-${coin?.id}-${i}`}
                className="flex items-center gap-2 px-5 h-9 border-r border-border/50 whitespace-nowrap"
              >
                <span className="text-[11px] font-bold text-foreground font-mono">{coin?.symbol}</span>
                <span className="text-[11px] font-semibold text-foreground tabular-nums font-mono">
                  {coin?.price}
                </span>
                <span
                  className={`flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${
                    coin?.positive ? 'text-crypto-green' : 'text-crypto-red'
                  }`}
                >
                  {coin?.positive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                  {coin?.change}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center gap-2 px-4 bg-card border-l border-border h-full">
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">Fear &amp; Greed Index:</span>
          <span className="text-[11px] font-bold text-crypto-green whitespace-nowrap">{fearGreed}</span>
        </div>
      </div>
    </div>
  );
}